import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ActivityIcon, CheckIcon, DnaIcon, ShieldCheckIcon } from 'lucide-react';
import { SnakButton } from './SnakButton';
import { ConsiderationVisual } from './ConsiderationVisual';
import { considerations, fictionalPatient } from '../../data/caprelsa';
import type { ConsiderationId } from '../../data/caprelsa';
const ICONS: Record<ConsiderationId, typeof ActivityIcon> = {
  progression: ActivityIcon,
  ret: DnaIcon,
  safety: ShieldCheckIcon
};
type InteractSceneProps = {
  active: ConsiderationId | null;
  explored: ConsiderationId[];
  onSelect: (id: ConsiderationId) => void;
  onNext: () => void;
};
export function InteractScene({
  active,
  explored,
  onSelect,
  onNext
}: InteractSceneProps) {
  const current = considerations.find(c => c.id === active);
  return <div className="flex h-full flex-col" data-id="element-524" data-mp="c4efiaxhxhef">
      <div className="flex items-center gap-3 rounded-2xl bg-cap-lilac p-3" data-id="element-525" data-mp="819fqg4svtth">
        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-cap-purple text-[13px] font-bold text-white" data-id="element-526" data-mp="83cfhrvr91be">A.</span>
        <div className="min-w-0 flex-1" data-id="element-527" data-mp="3alw304xak22v">
          <p className="flex items-baseline gap-2 text-[14px] font-bold text-cap-ink" data-id="element-528" data-mp="3ajjmlw76lzrx">
            {fictionalPatient.name}, {fictionalPatient.age}
            <span className="font-mono text-[8.5px] font-medium uppercase tracking-[0.16em] text-cap-grey" data-id="element-529" data-mp="30mwzf0rduwmq">Fictional</span>
          </p>
          <p className="text-[12.5px] text-cap-grey" data-id="element-530" data-mp="2mugxk3a7x4ne">{fictionalPatient.summary}</p>
          <div className="mt-1.5 flex flex-wrap gap-1" data-id="element-531" data-mp="2rnjriciwqg44">
            {fictionalPatient.tags.map(t => <span key={t} className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-cap-orange" data-id="element-532" data-mp="2cz6vxgnqeza5">
                {t}
              </span>)}
          </div>
        </div>
      </div>

      <h2 className="mt-5 text-[25px] font-extrabold leading-[1.08] tracking-[-0.03em] text-cap-purple" data-id="element-533" data-mp="23xz6zp73vasq">
        What matters when considering systemic treatment?
      </h2>
      <p className="mt-1.5 text-[13px] text-cap-grey" data-id="element-534" data-mp="eh3l9qivyozf">Tap each consideration to explore.</p>

      <div className="mt-4 grid grid-cols-3 gap-2" role="tablist" aria-label="Clinical considerations" data-id="element-535" data-mp="9oaiv9aw3sqi">
        {considerations.map(c => {
        const Icon = ICONS[c.id];
        const isActive = active === c.id;
        const done = explored.includes(c.id);
        return <button key={c.id} type="button" role="tab" aria-selected={isActive} onClick={() => onSelect(c.id)} className={`relative flex h-[84px] flex-col items-start justify-between rounded-2xl p-3 text-left transition-[background-color,box-shadow,transform] duration-150 active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-cap-gold ${isActive ? 'bg-cap-purple text-white' : 'bg-white text-cap-purple ring-1 ring-cap-line hover:ring-cap-purple/40'}`} data-id="element-536" data-mp="xf66rvc863sh">
              <Icon size={18} strokeWidth={2} aria-hidden="true" data-id="element-537" data-mp="1jomo3ad4433z" />
              <span className="text-[11.5px] font-bold leading-tight" data-id="element-538" data-mp="1olwmvgme5r4h">{c.short}</span>
              {done && <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-cap-gold text-white" data-id="element-539" data-mp="1snrl2c90zoe1">
                  <CheckIcon size={10} strokeWidth={3} aria-hidden="true" data-id="element-540" data-mp="1xg18nlmo4cd5" />
                </span>}
            </button>;
      })}
      </div>

      <div className="relative mt-3 min-h-[188px] rounded-2xl border-t-4 border-cap-gold bg-white p-4 shadow-[0_8px_24px_-12px_rgba(61,39,133,0.25)] ring-1 ring-cap-line" data-id="element-541" data-mp="27c4kxkb8hrdt">
        <AnimatePresence mode="wait" initial={false} data-id="element-542" data-mp="2gdc7540h71rj">
          {current ? <motion.div key={current.id} role="tabpanel" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.2
        }} data-id="element-543" data-mp="2gb9jurqpjmqi">
              <p className="text-[15px] font-bold leading-snug text-cap-ink" data-id="element-544" data-mp="3msz8lj07occj">{current.title}</p>
              <div className="mt-2" data-id="element-545" data-mp="3isnsmjisn07e">
                <ConsiderationVisual id={current.id} data-id="element-546" data-mp="nkds730z9ts" />
              </div>
              <p className="mt-2 text-[12.5px] leading-snug text-cap-grey" data-id="element-547" data-mp="lhs1z8wo2uv">{current.body}</p>
            </motion.div> : <motion.p key="empty" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex h-[150px] items-center justify-center text-center text-[13px] text-cap-grey/70" data-id="element-548" data-mp="34419e6q1poaw">
              Choose a consideration above.
            </motion.p>}
        </AnimatePresence>
      </div>

      <div className="mt-auto pt-4" data-id="element-549" data-mp="393npcnx9dine">
        <p className="mb-2 text-center font-mono text-[10px] tracking-[0.16em] text-cap-grey" data-id="element-550" data-mp="392m1fiv591o9">{explored.length} OF 3 EXPLORED</p>
        <SnakButton onClick={onNext} disabled={explored.length === 0} data-id="element-551" data-mp="2lbqdwxxq0smt">
          SEE THE EVIDENCE
        </SnakButton>
      </div>
    </div>;
}